import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { apiFetch } from "@/lib/api";
import { ArrowDown, ShieldCheck, BadgeCheck, Leaf } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [c, setC] = useState({});
  useEffect(() => {
    apiFetch("/api/content")
      .then(setC)
      .catch(() => {});
  }, []);
  const line1 = c.hero_line1 || "Bringing purity with devotion,";
  const line2 = c.hero_line2 || "rooted in";
  const accent = c.hero_accent || "bhakti.";
  const subtext =
    c.hero_subtext ||
    "Laxwedeepan was born from a personal space of peace, contentment and devotion — a desire to stay connected with things that feel pure, authentic and thoughtfully made. Rooted in Bhakti and inspired by traditional Indian living, our collection brings together pure Pooja and edible ghee, handcrafted mud toys and frames, matchsticks and fragrant flower oils — simple offerings created with purpose, purity and heart.";
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <RevealLine delay={0.35}>
            <span className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">
              ॥ शुभं करोति कल्याणम् ॥
            </span>
          </RevealLine>
          <h1 className="mt-8 font-display text-4xl font-medium leading-[1.1] text-brand-ink sm:text-5xl lg:text-[2.9rem] xl:text-[3.2rem]">
            <RevealLine delay={0.5}>
              <span className="lg:whitespace-nowrap">{line1}</span>
            </RevealLine>
            <RevealLine delay={0.65}>
              <span className="lg:whitespace-nowrap">{line2} <em className="italic text-brand-peach">{accent}</em></span>
            </RevealLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: EASE }}
            className="mt-10 max-w-xl text-base font-light leading-relaxed text-brand-ink/70 md:text-lg"
            data-testid="hero-subtext"
          >
            {subtext}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/collection"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-maroon px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold"
            >
              Explore the Collection
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.5} />
            </Link>
            <Link
              to="/philosophy"
              data-testid="hero-philosophy-button"
              className="rounded-full border border-brand-ink/20 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ink transition-colors duration-300 hover:border-brand-gold hover:text-brand-maroon"
            >
              Our Philosophy
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-brand-border pt-6 text-[11px] uppercase tracking-[0.2em] text-brand-ink/50"
            data-testid="hero-trust-strip"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-brand-gold" strokeWidth={1.5} /> GST Registered</span>
            <span className="flex items-center gap-2"><BadgeCheck className="h-3.5 w-3.5 text-brand-gold" strokeWidth={1.5} /> Trademark Registered</span>
            <span className="flex items-center gap-2"><Leaf className="h-3.5 w-3.5 text-brand-gold" strokeWidth={1.5} /> FSSAI Certified</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: logoY }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.6, ease: EASE }}
            className="relative mx-auto max-w-md"
          >
            <motion.div
              aria-hidden="true"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-6 rounded-full border border-dashed border-brand-gold/40"
            />
            <div className="arch-frame border border-brand-gold/60 p-3" data-testid="hero-logo-frame">
              <div className="arch-frame overflow-hidden bg-white">
                <img
                  src="/assets/laxwedeepan-logo.jpeg"
                  alt="Laxwedeepan — Nandi, the sacred cow, amidst flowers"
                  data-testid="hero-logo-image"
                  className="h-auto w-full mix-blend-multiply"
                />
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
              className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-brand-border bg-brand-ivory px-6 py-2.5 text-[10px] uppercase tracking-[0.3em] text-brand-maroon shadow-sm"
            >
              Poojan Dharm · Est. with devotion
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
